import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { ArrowRight } from 'lucide-react';
import Link from 'next/link';

// Static server-side components that don't need session
export function AgentsShowcase() {
    const scrollToTop = () => {
        if (typeof window !== 'undefined') {
            window.scrollTo({ top: 0, behavior: 'smooth' });
        }
    };

    return (
        <>
            {/* Confidential AI Agents Showcase */}
            <section className="px-6 py-24 lg:px-8 bg-gray-50">
                <div className="mx-auto max-w-6xl">
                    <div className="text-center mb-16">
                        <h2 className="text-3xl font-bold tracking-tight text-gray-900 sm:text-4xl">
                            Elite AI Agents from Top GTM Agencies
                        </h2>
                        <p className="mt-4 text-lg text-gray-600">
                            Every successful GTM strategy needs unique approaches. That's why we partner with leading agencies
                            to bring you diverse AI agents, each with proven methodologies and specialized expertise.
                        </p>
                    </div>

                    <div className="grid grid-cols-1 gap-8 lg:grid-cols-3">
                        {/* Confidential Agency 1 - SaaS & Enterprise */}
                        <Card className="border-0 shadow-lg">
                            <CardContent className="p-8">
                                <div className="mb-6">
                                    <div className="flex items-center gap-3 mb-3">
                                        <div className="w-12 h-12 bg-gradient-to-br from-blue-400 to-purple-500 rounded-lg flex items-center justify-center filter blur-sm relative overflow-hidden">
                                            <div className="absolute inset-0 bg-gradient-to-r from-blue-300/50 to-purple-400/50"></div>
                                            <div className="w-8 h-8 bg-white/80 rounded border-2 border-white/60"></div>
                                        </div>
                                        <div>
                                            <h3 className="text-xl font-bold text-gray-900 filter blur-sm">
                                                ████████████ AI
                                            </h3>
                                        </div>
                                    </div>
                                    <p className="text-sm text-gray-600 font-medium">SaaS & Enterprise Specialists</p>
                                    <p className="text-xs text-gray-500 mt-1">Operating since 2019 • 5+ years experience</p>
                                </div>

                                <div className="grid grid-cols-3 gap-3 mb-6">
                                    <div className="text-center">
                                        <div className="text-2xl font-bold text-teal-600">2,847</div>
                                        <div className="text-xs text-gray-600">TOTAL BOOKINGS</div>
                                    </div>
                                    <div className="text-center">
                                        <div className="text-2xl font-bold text-green-600">91%</div>
                                        <div className="text-xs text-gray-600">SHOW RATE</div>
                                    </div>
                                    <div className="text-center">
                                        <div className="text-2xl font-bold text-blue-600">$175</div>
                                        <div className="text-xs text-gray-600">PER BOOKING</div>
                                    </div>
                                </div>

                                <div className="mb-6">
                                    <p className="text-sm font-medium text-gray-900 mb-2">Specializes In:</p>
                                    <div className="flex flex-wrap gap-2">
                                        <Badge variant="secondary">SaaS Platforms</Badge>
                                        <Badge variant="secondary">FinTech</Badge>
                                        <Badge variant="secondary">AI/ML Solutions</Badge>
                                    </div>
                                </div>

                                <Button variant="outline" className="w-full" onClick={scrollToTop}>
                                    Sign Up to Contact
                                </Button>
                            </CardContent>
                        </Card>

                        {/* Confidential Agency 2 - SMB & Mid-Market */}
                        <Card className="border-0 shadow-lg">
                            <CardContent className="p-8">
                                <div className="mb-6">
                                    <div className="flex items-center gap-3 mb-3">
                                        <div className="w-12 h-12 bg-gradient-to-br from-green-400 to-teal-500 rounded-lg flex items-center justify-center filter blur-sm relative overflow-hidden">
                                            <div className="absolute inset-0 bg-gradient-to-r from-green-300/50 to-teal-400/50"></div>
                                            <div className="w-8 h-8 bg-white/80 rounded-full border-2 border-white/60"></div>
                                        </div>
                                        <div>
                                            <h3 className="text-xl font-bold text-gray-900 filter blur-sm">
                                                █████████ AI
                                            </h3>
                                        </div>
                                    </div>
                                    <p className="text-sm text-gray-600 font-medium">SMB & Mid-Market Experts</p>
                                    <p className="text-xs text-gray-500 mt-1">Operating since 2020 • 4+ years experience</p>
                                </div>

                                <div className="grid grid-cols-3 gap-3 mb-6">
                                    <div className="text-center">
                                        <div className="text-2xl font-bold text-teal-600">1,963</div>
                                        <div className="text-xs text-gray-600">TOTAL BOOKINGS</div>
                                    </div>
                                    <div className="text-center">
                                        <div className="text-2xl font-bold text-green-600">93%</div>
                                        <div className="text-xs text-gray-600">SHOW RATE</div>
                                    </div>
                                    <div className="text-center">
                                        <div className="text-2xl font-bold text-blue-600">$125</div>
                                        <div className="text-xs text-gray-600">PER BOOKING</div>
                                    </div>
                                </div>

                                <div className="mb-6">
                                    <p className="text-sm font-medium text-gray-900 mb-2">Specializes In:</p>
                                    <div className="flex flex-wrap gap-2">
                                        <Badge variant="secondary">Healthcare Tech</Badge>
                                        <Badge variant="secondary">E-commerce</Badge>
                                        <Badge variant="secondary">Manufacturing</Badge>
                                    </div>
                                </div>

                                <Button variant="outline" className="w-full" onClick={scrollToTop}>
                                    Sign Up to Contact
                                </Button>
                            </CardContent>
                        </Card>

                        {/* Confidential Agency 3 - Specialized Verticals */}
                        <Card className="border-0 shadow-lg">
                            <CardContent className="p-8">
                                <div className="mb-6">
                                    <div className="flex items-center gap-3 mb-3">
                                        <div className="w-12 h-12 bg-gradient-to-br from-orange-400 to-red-500 rounded-lg flex items-center justify-center filter blur-sm relative overflow-hidden">
                                            <div className="absolute inset-0 bg-gradient-to-r from-orange-300/50 to-red-400/50"></div>
                                            <div className="w-6 h-6 bg-white/80 rounded-sm rotate-45 border-2 border-white/60"></div>
                                        </div>
                                        <div>
                                            <h3 className="text-xl font-bold text-gray-900 filter blur-sm">
                                                ███████ AI
                                            </h3>
                                        </div>
                                    </div>
                                    <p className="text-sm text-gray-600 font-medium">Specialized Vertical Markets</p>
                                    <p className="text-xs text-gray-500 mt-1">Operating since 2021 • 3+ years experience</p>
                                </div>

                                <div className="grid grid-cols-3 gap-3 mb-6">
                                    <div className="text-center">
                                        <div className="text-2xl font-bold text-teal-600">1,521</div>
                                        <div className="text-xs text-gray-600">TOTAL BOOKINGS</div>
                                    </div>
                                    <div className="text-center">
                                        <div className="text-2xl font-bold text-green-600">95%</div>
                                        <div className="text-xs text-gray-600">SHOW RATE</div>
                                    </div>
                                    <div className="text-center">
                                        <div className="text-2xl font-bold text-blue-600">$200</div>
                                        <div className="text-xs text-gray-600">PER BOOKING</div>
                                    </div>
                                </div>

                                <div className="mb-6">
                                    <p className="text-sm font-medium text-gray-900 mb-2">Specializes In:</p>
                                    <div className="flex flex-wrap gap-2">
                                        <Badge variant="secondary">Renewable Energy</Badge>
                                        <Badge variant="secondary">EdTech</Badge>
                                        <Badge variant="secondary">Logistics</Badge>
                                    </div>
                                </div>

                                <Button variant="outline" className="w-full" onClick={scrollToTop}>
                                    Sign Up to Contact
                                </Button>
                            </CardContent>
                        </Card>
                    </div>

                    {/* Call to Action */}
                    <div className="text-center mt-12">
                        <div className="bg-white rounded-2xl p-8 shadow-lg border border-gray-200 max-w-2xl mx-auto">
                            <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-teal-100 mb-4">
                                <svg className="w-8 h-8 text-teal-600" fill="currentColor" viewBox="0 0 20 20">
                                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                                </svg>
                            </div>
                            <h3 className="text-xl font-bold text-gray-900 mb-2">
                                Access Elite AI Agents
                            </h3>
                            <p className="text-gray-600 mb-6">
                                Get access to our network of confidential AI agents from top-tier GTM agencies.
                                Sign up to see full profiles and start booking meetings.
                            </p>
                            <Button size="lg" className="bg-gradient-to-r from-teal-500 to-green-500 hover:from-teal-600 hover:to-green-600 text-white px-8 py-4" onClick={scrollToTop}>
                                Sign Up to Contact Agents
                                <ArrowRight className="ml-2 h-5 w-5" />
                            </Button>
                        </div>
                    </div>
                </div>
            </section>

            {/* Old Way vs NOS Way - Comparison Table */}
            <section className="px-6 py-24 lg:px-8 bg-white">
                <div className="mx-auto max-w-6xl">
                    <div className="text-center mb-16">
                        <h2 className="text-3xl font-bold tracking-tight text-gray-900 sm:text-4xl">
                            A Fundamentally Better Way to Grow
                        </h2>
                        <p className="mt-4 text-lg text-gray-600">
                            Traditional sales outreach is broken. It's costly, inefficient, and built on promises. We fixed it.
                        </p>
                    </div>

                    {/* Comparison Table */}
                    <div className="overflow-hidden bg-white shadow-xl rounded-2xl border border-gray-200">
                        <div className="grid grid-cols-1 lg:grid-cols-3">
                            {/* Header Row */}
                            <div className="bg-gray-50 p-6 border-b border-gray-200 lg:border-b-0 lg:border-r">
                                <div className="text-center">
                                    <h3 className="text-lg font-semibold text-gray-900">Comparison</h3>
                                </div>
                            </div>
                            <div className="bg-red-50 p-6 border-b border-gray-200 lg:border-b-0 lg:border-r border-red-200">
                                <div className="text-center">
                                    <h3 className="text-lg font-semibold text-red-800">Traditional Agencies</h3>
                                    <p className="text-sm text-red-600 mt-1">Expensive & Risky</p>
                                </div>
                            </div>
                            <div className="bg-green-50 p-6 border-green-200">
                                <div className="text-center">
                                    <h3 className="text-lg font-semibold text-green-800">NOS Agents</h3>
                                    <p className="text-sm text-green-600 mt-1">Performance-Based</p>
                                </div>
                            </div>

                            {/* Cost Per Lead Row */}
                            <div className="p-6 border-b border-gray-200 lg:border-r bg-gray-50">
                                <div className="font-medium text-gray-900">Cost Per Qualified Meeting</div>
                            </div>
                            <div className="p-6 border-b border-gray-200 lg:border-r bg-red-50 border-red-200">
                                <div className="text-red-700">
                                    <div className="font-semibold text-lg text-red-800">$1,000+ retainer</div>
                                    <div className="text-sm">+ $500 per lead</div>
                                    <div className="text-xs text-red-600 mt-1">No guarantee of results</div>
                                </div>
                            </div>
                            <div className="p-6 border-b border-gray-200 bg-green-50 border-green-200">
                                <div className="text-green-700">
                                    <div className="font-semibold text-lg text-green-800">$150 average</div>
                                    <div className="text-sm">Per confirmed meeting only</div>
                                    <div className="text-xs text-green-600 mt-1">100% performance-based</div>
                                </div>
                            </div>

                            {/* Setup & Risk Row */}
                            <div className="p-6 border-b border-gray-200 lg:border-r bg-gray-50">
                                <div className="font-medium text-gray-900">Setup & Risk</div>
                            </div>
                            <div className="p-6 border-b border-gray-200 lg:border-r bg-red-50 border-red-200">
                                <div className="text-red-700">
                                    <div className="font-semibold">High upfront cost</div>
                                    <div className="text-sm">3-6 month contracts</div>
                                    <div className="text-xs text-red-600 mt-1">Pay regardless of results</div>
                                </div>
                            </div>
                            <div className="p-6 border-b border-gray-200 bg-green-50 border-green-200">
                                <div className="text-green-700">
                                    <div className="font-semibold">Zero upfront cost</div>
                                    <div className="text-sm">No long-term contracts</div>
                                    <div className="text-xs text-green-600 mt-1">Only pay for confirmed meetings</div>
                                </div>
                            </div>

                            {/* Quality Row */}
                            <div className="p-6 border-b border-gray-200 lg:border-r bg-gray-50">
                                <div className="font-medium text-gray-900">Message Quality</div>
                            </div>
                            <div className="p-6 border-b border-gray-200 lg:border-r bg-red-50 border-red-200">
                                <div className="text-red-700">
                                    <div className="font-semibold">Generic templates</div>
                                    <div className="text-sm">Low personalization</div>
                                    <div className="text-xs text-red-600 mt-1">Hurts brand reputation</div>
                                </div>
                            </div>
                            <div className="p-6 border-b border-gray-200 bg-green-50 border-green-200">
                                <div className="text-green-700">
                                    <div className="font-semibold">AI-powered personalization</div>
                                    <div className="text-sm">Hyper-relevant outreach</div>
                                    <div className="text-xs text-green-600 mt-1">Protects brand reputation</div>
                                </div>
                            </div>

                            {/* Scalability Row */}
                            <div className="p-6 lg:border-r bg-gray-50">
                                <div className="font-medium text-gray-900">Scalability</div>
                            </div>
                            <div className="p-6 lg:border-r bg-red-50 border-red-200">
                                <div className="text-red-700">
                                    <div className="font-semibold">Manual limitations</div>
                                    <div className="text-sm">Team capacity bottleneck</div>
                                    <div className="text-xs text-red-600 mt-1">Higher costs to scale</div>
                                </div>
                            </div>
                            <div className="p-6 bg-green-50 border-green-200">
                                <div className="text-green-700">
                                    <div className="font-semibold">Infinite AI capacity</div>
                                    <div className="text-sm">24/7 automated outreach</div>
                                    <div className="text-xs text-green-600 mt-1">Scale without cost increase</div>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Call to Action under table */}
                    <div className="text-center mt-12">
                        <p className="text-lg text-gray-600 mb-6">
                            Stop overpaying for poor results. Get better meetings at a fraction of the cost.
                        </p>
                        <Button size="lg" className="bg-gradient-to-r from-teal-500 to-green-500 hover:from-teal-600 hover:to-green-600 text-white px-8 py-4" onClick={scrollToTop}>
                            See How Much You'll Save
                            <ArrowRight className="ml-2 h-5 w-5" />
                        </Button>
                    </div>
                </div>
            </section>

            {/* Footer */}
            <footer className="bg-white border-t">
                <div className="mx-auto max-w-6xl px-6 py-12 lg:px-8">
                    <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
                        <div className="md:col-span-2">
                            <div className="flex items-center space-x-3 mb-4">
                                <div className="w-12 h-12 bg-gradient-to-br from-teal-500 to-green-500 rounded-xl flex items-center justify-center shadow-md">
                                    <span className="text-white font-bold text-lg tracking-wider">NOS</span>
                                </div>
                                <span className="text-xl font-bold text-gray-900">Agents</span>
                            </div>
                            <p className="text-gray-600 max-w-md">
                                Effortless bookings. Guaranteed ROI. Pay only for performance.
                            </p>
                        </div>

                        <div>
                            <h3 className="text-sm font-semibold text-gray-900 mb-4">Platform</h3>
                            <ul className="space-y-2 text-sm text-gray-600">
                                <li><Link href="#" className="hover:text-teal-600">How It Works (Clients)</Link></li>
                                <li><Link href="/agencies" className="hover:text-teal-600">For Agencies</Link></li>
                                <li><Link href="#" className="hover:text-teal-600">Pricing</Link></li>
                            </ul>
                        </div>

                        <div>
                            <h3 className="text-sm font-semibold text-gray-900 mb-4">Company</h3>
                            <ul className="space-y-2 text-sm text-gray-600">
                                <li><Link href="#" className="hover:text-teal-600">About Us</Link></li>
                                <li><Link href="#" className="hover:text-teal-600">Contact</Link></li>
                                <li><Link href="/privacy" className="hover:text-teal-600">Privacy Policy</Link></li>
                                <li><Link href="/terms" className="hover:text-teal-600">Terms of Service</Link></li>
                            </ul>
                        </div>
                    </div>

                    <div className="border-t mt-8 pt-8 text-center">
                        <p className="text-sm text-gray-600">
                            © 2025 NOS Agents. All rights reserved.
                        </p>
                    </div>
                </div>
            </footer>
        </>
    );
}
